import {
  AddressCard,
  CollapsibleCard,
  IconButton,
  TableCell,
  TableRow,
} from '@atgfr8/atg-ui-tailwind';
import { BasicModal, BasicTable, Button } from '@components';
import { Contact } from '@store/customers/customerDetails';
import { closeModal, openModal } from '@store/modals';
import { prospectDetailsStore, updateProspectDetails } from '@store/prospect';
import { DeleteIcon, AccountPlusIcon } from '@atgfr8/icons';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { AddNewContact } from '@views/order/components';
import { cloneDeep } from 'lodash';
import { Component, createSignal, Index } from 'solid-js';

export const ProspectDetailsContactCard: Component = () => {
  const [selectedContact, setSelectedContact] = createSignal<Contact | null>(
    null,
  );

  const contactModalId = 'contactModalId';

  const columns = [{ name: 'Name' }, { name: 'Type' }, { name: 'Delete' }];

  const handleProspectContactData = (values: Contact) => {
    const updatedValues = {
      ...values,
      phone: values.phone1,
      operationType: !Boolean(values.id) ? 'Insert' : values.operationType,
    };
    const contacts = prospectDetailsStore.prospectDetails.contacts.map(
      (contact) =>
        contact.id === values.id
          ? {
              ...contact,
              ...updatedValues,
              operationType:
                values.operationType !== 'Insert' ? 'Update' : 'Insert',
            }
          : contact,
    );

    if (!contacts.some((contact) => contact.id === values.id)) {
      contacts.push({
        ...updatedValues,
        id: contacts.length + 1,
        uniqueId: null,
        objectType: '',
      });
    }

    updateProspectDetails({ contacts });
    handleClose();
  };

  const handleDeleteContact = (id: number) => {
    const contacts = cloneDeep(prospectDetailsStore.prospectDetails.contacts);
    const idx = contacts.findIndex((x) => x.id === id);
    if (contacts[idx].operationType === 'Insert') {
      contacts.splice(idx, 1);
    } else {
      contacts[idx].status = 'Inactive';
      contacts[idx].operationType = 'Update';
    }
    updateProspectDetails({ contacts });
  };

  const handleClose = () => {
    closeModal(contactModalId);
    setSelectedContact(null);
  };

  const onEditContact = (contact: Contact) => {
    setSelectedContact(contact);
    openModal(contactModalId);
  };

  const activeContacts = () =>
    prospectDetailsStore.prospectDetails.contacts.filter(
      (contact) => contact.status !== 'Inactive',
    );

  return (
    <CollapsibleCard
      startTitle="Contact"
      accordion={true}
      loading={prospectDetailsStore.loading}
      maxContentHeight={300}
      endIcon={
        <Button
          variant="contained"
          color="primary"
          label="New Contact"
          onClick={() => openModal(contactModalId)}
          startIcon={<AccountPlusIcon size={20} />}
          dataTestId={formatDataTestId('New Contact', 'button')}
        />
      }
      class="mb-2"
    >
      <BasicTable columns={columns}>
        <Index each={activeContacts()}>
          {(item) => (
            <TableRow
              class="hover:bg-[#D2E2ED] cursor-pointer"
              onClick={() => onEditContact(item())}
            >
              <TableCell>
                <AddressCard
                  isCustomerContact={true}
                  mode="regular"
                  contact={{
                    userName: item().userName,
                    name: item().name,
                    email: item().email,
                    phone1: item().phone1,
                    extension1: item().extension1,
                    fax: item().fax,
                  }}
                  contactType={item().type}
                />
              </TableCell>
              <TableCell>{item().type}</TableCell>
              <TableCell>
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteContact(item().id);
                  }}
                >
                  <IconButton
                    dataTestId={formatDataTestId(
                      `Delete contact ${item().id}`,
                      'button',
                    )}
                  >
                    <DeleteIcon class="text-[#B00020]!" />
                  </IconButton>
                </div>
              </TableCell>
            </TableRow>
          )}
        </Index>
      </BasicTable>
      <BasicModal
        id={contactModalId}
        title={`${selectedContact() ? 'Edit' : 'Add'} Contact`}
        width="fit-content"
        footer={false}
        showClose={false}
        onClose={() => handleClose()}
      >
        <AddNewContact
          modalId={contactModalId}
          editObject={selectedContact}
          handleClose={handleClose}
          onSubmit={(values) => handleProspectContactData(values as Contact)}
          initiatePath="ProspectDetails"
        />
      </BasicModal>
    </CollapsibleCard>
  );
};
